import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { currencyToNumber } from "helpers/utils";
import { accessFromUser } from "helpers/permissions";
import { readUserSession } from "services/access";
import { userCanViewSaldoEfi } from "components/EfiAccountBalanceCard/saldoEfiPermissions";
import { saldoDisponivelTransferencia } from "../helpers/contas-financeiras-saldo-efi";
import {
  contaRepassePadrao,
  isContaCaixaPadrao,
  isContaGatewayEfi,
  type ConciliacaoSaldoEfiApi,
} from "../types/contas-financeiras.types";
import {
  atualizarContaFinanceiraApi,
  conciliarSaldoContaApi,
  conciliarSaldoGatewayEfiApi,
  criarContaFinanceiraApi,
  excluirContaFinanceiraApi,
  listarContasFinanceirasGerencial,
  montarPayloadContaFinanceira,
  transferirEntreContasFinanceirasApi,
} from "../services/contas-financeiras-admin.service";
import type { ExcluirContaFinanceiraDialogState } from "../components/ExcluirContaFinanceiraDialog";
import type { TransferenciaContaFinanceiraDialogState } from "../components/TransferenciaContaFinanceiraDialog";
import { formPadraoTransferenciaConta, observacaoRepasseManual } from "../components/TransferenciaContaFinanceiraDialog";
import type { ConciliarSaldoContaDialogState } from "../components/ConciliarSaldoContaDialog";
import { formPadraoConciliarSaldoConta } from "../components/ConciliarSaldoContaDialog";
import type {
  ConciliarSaldoContaFormPayload,
  ContaFinanceiraFormPayload,
  ContaFinanceiraGerencialApi,
  ContasFinanceirasGerencialApi,
  TransferenciaContaFinanceiraFormPayload,
  TipoContaFinanceiraApi,
} from "../types/contas-financeiras.types";

export function formPadraoContaFinanceira(
  partial?: Partial<ContaFinanceiraFormPayload>,
): ContaFinanceiraFormPayload {
  return {
    nome: "",
    tipo: "BANCO",
    saldoInicial: "0",
    ehPadrao: false,
    ehContaRepasse: false,
    ...partial,
  };
}

export function contaParaForm(
  c: ContaFinanceiraGerencialApi,
): ContaFinanceiraFormPayload {
  return {
    nome: c.nome,
    tipo: c.tipo,
    saldoInicial: c.saldoInicial.toFixed(2),
    ehPadrao: c.ehPadrao,
    ehContaRepasse: c.ehContaRepasse ?? isContaCaixaPadrao(c),
  };
}

export type UseContasFinanceirasGerencialResult = {
  data: ContasFinanceirasGerencialApi | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  dialogOpen: boolean;
  editId: string | null;
  form: ContaFinanceiraFormPayload;
  setForm: Dispatch<SetStateAction<ContaFinanceiraFormPayload>>;
  salvando: boolean;
  dialogError: string | null;
  abrirNova: () => void;
  abrirEditar: (conta: ContaFinanceiraGerencialApi) => void;
  fecharDialog: () => void;
  salvar: () => Promise<void>;
  marcarPadrao: (conta: ContaFinanceiraGerencialApi) => Promise<void>;
  alternarAtivo: (conta: ContaFinanceiraGerencialApi) => Promise<void>;
  excluirDialog: ExcluirContaFinanceiraDialogState;
  excluirDialogError: string | null;
  excluindo: boolean;
  abrirExcluir: (conta: ContaFinanceiraGerencialApi) => void;
  fecharExcluir: () => void;
  confirmarExcluir: () => Promise<void>;
  transferenciaDialog: TransferenciaContaFinanceiraDialogState;
  transferenciaForm: TransferenciaContaFinanceiraFormPayload;
  setTransferenciaForm: Dispatch<SetStateAction<TransferenciaContaFinanceiraFormPayload>>;
  transferenciaError: string | null;
  transferindo: boolean;
  abrirTransferencia: (conta: ContaFinanceiraGerencialApi) => void;
  fecharTransferencia: () => void;
  confirmarTransferencia: () => Promise<void>;
  contasDestinoTransferencia: ContaFinanceiraGerencialApi[];
  conciliacaoDialog: ConciliarSaldoContaDialogState;
  conciliacaoForm: ConciliarSaldoContaFormPayload;
  setConciliacaoForm: Dispatch<SetStateAction<ConciliarSaldoContaFormPayload>>;
  conciliacaoError: string | null;
  conciliando: boolean;
  abrirConciliacao: (conta: ContaFinanceiraGerencialApi) => void;
  fecharConciliacao: () => void;
  confirmarConciliacao: () => Promise<void>;
  podeVerSaldoEfi: boolean;
  atualizandoSaldoEfi: boolean;
  atualizarSaldoEfi: () => Promise<void>;
  conciliandoSaldoEfi: boolean;
  conciliarSaldoEfi: () => Promise<void>;
  abrirRepasseCaixa: (conta: ContaFinanceiraGerencialApi) => void;
  conciliacaoFeedback: ConciliacaoSaldoEfiApi | null;
  limparConciliacaoFeedback: () => void;
};

export function useContasFinanceirasGerencial(): UseContasFinanceirasGerencialResult {
  const [data, setData] = useState<ContasFinanceirasGerencialApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<ContaFinanceiraFormPayload>(() =>
    formPadraoContaFinanceira(),
  );
  const [salvando, setSalvando] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [excluirDialog, setExcluirDialog] =
    useState<ExcluirContaFinanceiraDialogState>({ open: false, conta: null });
  const [excluirDialogError, setExcluirDialogError] = useState<string | null>(
    null,
  );
  const [excluindo, setExcluindo] = useState(false);
  const [transferenciaDialog, setTransferenciaDialog] =
    useState<TransferenciaContaFinanceiraDialogState>({
      open: false,
      contaOrigem: null,
    });
  const [transferenciaForm, setTransferenciaForm] =
    useState<TransferenciaContaFinanceiraFormPayload>(() =>
      formPadraoTransferenciaConta(),
    );
  const [transferenciaError, setTransferenciaError] = useState<string | null>(
    null,
  );
  const [transferindo, setTransferindo] = useState(false);
  const [conciliacaoDialog, setConciliacaoDialog] =
    useState<ConciliarSaldoContaDialogState>({ open: false, conta: null });
  const [conciliacaoForm, setConciliacaoForm] =
    useState<ConciliarSaldoContaFormPayload>(() => formPadraoConciliarSaldoConta());
  const [conciliacaoError, setConciliacaoError] = useState<string | null>(null);
  const [conciliando, setConciliando] = useState(false);
  const [atualizandoSaldoEfi, setAtualizandoSaldoEfi] = useState(false);
  const [conciliandoSaldoEfi, setConciliandoSaldoEfi] = useState(false);
  const [conciliacaoFeedback, setConciliacaoFeedback] =
    useState<ConciliacaoSaldoEfiApi | null>(null);

  const podeVerSaldoEfi = useMemo(
    () => userCanViewSaldoEfi(accessFromUser(readUserSession())),
    [],
  );

  const aplicarRespostaGerencial = useCallback(
    (res: ContasFinanceirasGerencialApi) => {
      setData({
        ...res,
        saldoConsolidadoExtrato:
          res.saldoConsolidadoExtrato ?? res.saldoConsolidado,
      });
    },
    [],
  );

  const limparConciliacaoFeedback = useCallback(() => {
    setConciliacaoFeedback(null);
  }, []);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  const atualizarSaldoEfi = useCallback(async () => {
    if (!podeVerSaldoEfi) return;
    setAtualizandoSaldoEfi(true);
    try {
      const { data: res, status, error: err } =
        await listarContasFinanceirasGerencial();
      if (status === 200 && res) {
        aplicarRespostaGerencial(res);
      } else {
        setError(err ?? "Não foi possível atualizar saldo Efí.");
      }
    } finally {
      setAtualizandoSaldoEfi(false);
    }
  }, [aplicarRespostaGerencial, podeVerSaldoEfi]);

  const conciliarSaldoEfi = useCallback(async () => {
    if (!podeVerSaldoEfi) return;
    setConciliandoSaldoEfi(true);
    try {
      const { data: res, status, error: err } = await conciliarSaldoGatewayEfiApi();
      if (status !== 200 || !res) {
        setError(err ?? "Não foi possível conciliar saldo Efí.");
        return;
      }
      if (res.aplicada) {
        setConciliacaoFeedback(res);
        refetch();
      } else {
        setError(res.motivo ?? "Nenhum ajuste necessário.");
      }
    } finally {
      setConciliandoSaldoEfi(false);
    }
  }, [podeVerSaldoEfi, refetch]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      const { data: res, status, error: err } =
        await listarContasFinanceirasGerencial();
      if (cancelled) return;
      if (status === 200 && res) {
        aplicarRespostaGerencial(res);
      } else {
        setData(null);
        setError(err ?? "Não foi possível carregar as contas financeiras.");
      }
      setLoading(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [tick, aplicarRespostaGerencial]);

  const fecharDialog = useCallback(() => {
    if (salvando) return;
    setDialogOpen(false);
    setEditId(null);
    setDialogError(null);
  }, [salvando]);

  const abrirNova = useCallback(() => {
    setEditId(null);
    setForm(formPadraoContaFinanceira());
    setDialogError(null);
    setDialogOpen(true);
  }, []);

  const abrirEditar = useCallback((conta: ContaFinanceiraGerencialApi) => {
    setEditId(conta.id);
    setForm(contaParaForm(conta));
    setDialogError(null);
    setDialogOpen(true);
  }, []);

  const salvar = useCallback(async () => {
    setDialogError(null);
    const payload = montarPayloadContaFinanceira(form);
    if (!payload.nome) {
      setDialogError("Informe o nome da conta.");
      return;
    }
    setSalvando(true);
    try {
      if (editId) {
        const { status, error: err } = await atualizarContaFinanceiraApi(
          editId,
          payload,
        );
        if (status !== 200) {
          setDialogError(err ?? "Não foi possível salvar a conta.");
          return;
        }
      } else {
        const { status, error: err } = await criarContaFinanceiraApi(payload);
        if (status !== 201) {
          setDialogError(err ?? "Não foi possível criar a conta.");
          return;
        }
      }
      setDialogOpen(false);
      setEditId(null);
      refetch();
    } finally {
      setSalvando(false);
    }
  }, [editId, form, refetch]);

  const marcarPadrao = useCallback(
    async (conta: ContaFinanceiraGerencialApi) => {
      const { status, error: err } = await atualizarContaFinanceiraApi(conta.id, {
        ehPadrao: true,
      });
      if (status !== 200) {
        setError(err ?? "Não foi possível definir conta padrão.");
        return;
      }
      refetch();
    },
    [refetch],
  );

  const alternarAtivo = useCallback(
    async (conta: ContaFinanceiraGerencialApi) => {
      const { status, error: err } = await atualizarContaFinanceiraApi(conta.id, {
        ativo: !conta.ativo,
      });
      if (status !== 200) {
        setError(err ?? "Não foi possível alterar o status da conta.");
        return;
      }
      refetch();
    },
    [refetch],
  );

  const abrirExcluir = useCallback((conta: ContaFinanceiraGerencialApi) => {
    setExcluirDialogError(null);
    setExcluirDialog({ open: true, conta });
  }, []);

  const fecharExcluir = useCallback(() => {
    if (excluindo) return;
    setExcluirDialog({ open: false, conta: null });
    setExcluirDialogError(null);
  }, [excluindo]);

  const confirmarExcluir = useCallback(async () => {
    const id = excluirDialog.conta?.id;
    if (!id) return;
    setExcluirDialogError(null);
    setExcluindo(true);
    try {
      const { status, error: err } = await excluirContaFinanceiraApi(id);
      if (status !== 200) {
        setExcluirDialogError(err ?? "Não foi possível excluir a conta.");
        return;
      }
      setExcluirDialog({ open: false, conta: null });
      refetch();
    } finally {
      setExcluindo(false);
    }
  }, [excluirDialog.conta?.id, refetch]);

  const contasDestinoTransferencia = useMemo(() => {
    const origemId = transferenciaDialog.contaOrigem?.id;
    if (!origemId || !data) return [];
    return data.contas.filter((c) => c.ativo && c.id !== origemId);
  }, [data, transferenciaDialog.contaOrigem?.id]);

  const abrirTransferencia = useCallback(
    (conta: ContaFinanceiraGerencialApi) => {
      const destinos =
        data?.contas.filter((c) => c.ativo && c.id !== conta.id) ?? [];
      setTransferenciaError(null);
      setTransferenciaForm(
        formPadraoTransferenciaConta({
          contaDestinoId: destinos.length === 1 ? destinos[0].id : "",
        }),
      );
      setTransferenciaDialog({ open: true, contaOrigem: conta, modoRepasse: false });
    },
    [data],
  );

  const abrirRepasseCaixa = useCallback(
    (contaEfi: ContaFinanceiraGerencialApi) => {
      const destinoPadrao = data?.contas ? contaRepassePadrao(data.contas) : undefined;
      const saldoSugerido = saldoDisponivelTransferencia(contaEfi);
      setTransferenciaError(null);
      setTransferenciaForm(
        formPadraoTransferenciaConta({
          contaDestinoId: destinoPadrao?.id ?? "",
          valor:
            saldoSugerido > 0
              ? saldoSugerido.toFixed(2).replace(".", ",")
              : "",
          observacao: observacaoRepasseManual(destinoPadrao?.nome ?? "destino"),
        }),
      );
      setTransferenciaDialog({
        open: true,
        contaOrigem: contaEfi,
        modoRepasse: true,
      });
    },
    [data],
  );

  const fecharTransferencia = useCallback(() => {
    if (transferindo) return;
    setTransferenciaDialog({ open: false, contaOrigem: null, modoRepasse: false });
    setTransferenciaError(null);
    setTransferenciaForm(formPadraoTransferenciaConta());
  }, [transferindo]);

  const confirmarTransferencia = useCallback(async () => {
    const origemId = transferenciaDialog.contaOrigem?.id;
    const origem =
      data?.contas.find((c) => c.id === origemId) ??
      transferenciaDialog.contaOrigem;
    if (!origem) return;

    const valor = Math.round(currencyToNumber(transferenciaForm.valor) * 100) / 100;
    if (valor <= 0) {
      setTransferenciaError("Informe um valor maior que zero.");
      return;
    }
    if (!transferenciaForm.contaDestinoId) {
      setTransferenciaError("Selecione a conta de destino.");
      return;
    }
    const saldoDisp = saldoDisponivelTransferencia(origem);
    if (valor > saldoDisp) {
      setTransferenciaError(
        `Saldo insuficiente. Disponível: R$ ${saldoDisp.toFixed(2).replace(".", ",")}.`,
      );
      return;
    }

    setTransferenciaError(null);
    setTransferindo(true);
    try {
      const { status, error: err } = await transferirEntreContasFinanceirasApi({
        contaOrigemId: origem.id,
        contaDestinoId: transferenciaForm.contaDestinoId,
        valor,
        observacao: transferenciaForm.observacao.trim() || undefined,
      });
      if (status !== 201) {
        setTransferenciaError(err ?? "Não foi possível concluir a transferência.");
        return;
      }
      setTransferenciaDialog({ open: false, contaOrigem: null, modoRepasse: false });
      setTransferenciaForm(formPadraoTransferenciaConta());
      refetch();
    } finally {
      setTransferindo(false);
    }
  }, [data, refetch, transferenciaDialog.contaOrigem, transferenciaForm]);

  const abrirConciliacao = useCallback((conta: ContaFinanceiraGerencialApi) => {
    setConciliacaoError(null);
    const saldoEfi =
      isContaGatewayEfi(conta) && conta.saldoEfiDisponivel != null
        ? conta.saldoEfiDisponivel.toFixed(2).replace(".", ",")
        : "";
    setConciliacaoForm(
      formPadraoConciliarSaldoConta(
        saldoEfi
          ? {
              saldoReal: saldoEfi,
              observacao: "Alinhamento extrato em homologação Efí.",
            }
          : undefined,
      ),
    );
    setConciliacaoDialog({ open: true, conta });
  }, []);

  const fecharConciliacao = useCallback(() => {
    if (conciliando) return;
    setConciliacaoDialog({ open: false, conta: null });
    setConciliacaoError(null);
    setConciliacaoForm(formPadraoConciliarSaldoConta());
  }, [conciliando]);

  const confirmarConciliacao = useCallback(async () => {
    const contaId = conciliacaoDialog.conta?.id;
    if (!contaId) return;

    const saldoReal = Math.round(currencyToNumber(conciliacaoForm.saldoReal) * 100) / 100;
    if (!Number.isFinite(saldoReal) || saldoReal < 0) {
      setConciliacaoError("Informe um saldo real válido (≥ 0).");
      return;
    }

    setConciliacaoError(null);
    setConciliando(true);
    try {
      const { data: res, status, error: err } = await conciliarSaldoContaApi(contaId, {
        saldoReal,
        observacao: conciliacaoForm.observacao.trim() || undefined,
      });
      if (status !== 200 || !res) {
        setConciliacaoError(err ?? "Não foi possível conciliar o saldo.");
        return;
      }
      if (res.aplicada) {
        setConciliacaoFeedback(res);
        setConciliacaoDialog({ open: false, conta: null });
        setConciliacaoForm(formPadraoConciliarSaldoConta());
        refetch();
      } else {
        setConciliacaoError(res.motivo ?? "Nenhum ajuste necessário.");
      }
    } finally {
      setConciliando(false);
    }
  }, [conciliacaoDialog.conta?.id, conciliacaoForm, refetch]);

  return {
    data,
    loading,
    error,
    refetch,
    dialogOpen,
    editId,
    form,
    setForm,
    salvando,
    dialogError,
    abrirNova,
    abrirEditar,
    fecharDialog,
    salvar,
    marcarPadrao,
    alternarAtivo,
    excluirDialog,
    excluirDialogError,
    excluindo,
    abrirExcluir,
    fecharExcluir,
    confirmarExcluir,
    transferenciaDialog,
    transferenciaForm,
    setTransferenciaForm,
    transferenciaError,
    transferindo,
    abrirTransferencia,
    fecharTransferencia,
    confirmarTransferencia,
    contasDestinoTransferencia,
    conciliacaoDialog,
    conciliacaoForm,
    setConciliacaoForm,
    conciliacaoError,
    conciliando,
    abrirConciliacao,
    fecharConciliacao,
    confirmarConciliacao,
    podeVerSaldoEfi,
    atualizandoSaldoEfi,
    atualizarSaldoEfi,
    conciliandoSaldoEfi,
    conciliarSaldoEfi,
    abrirRepasseCaixa,
    conciliacaoFeedback,
    limparConciliacaoFeedback,
  };
}
